import { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const FORM_URL = 'https://readdy.ai/api/form/d6tkv8q4of3kuoicn7dg';

const MapContact = () => {
  const [visible, setVisible] = useState(false);
  const [formState, setFormState] = useState({ name: '', phone: '', email: '', moveDate: '', serviceType: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.05 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const body = new URLSearchParams({
        name: formState.name,
        phone: formState.phone,
        email: formState.email,
        moveDate: formState.moveDate,
        serviceType: formState.serviceType,
        message: formState.message,
      });
      await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section ref={ref} className="py-24 bg-cream" id="contact">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="h-0.5 w-8 bg-teal"></div>
            <span className="text-teal font-body text-xs tracking-widest uppercase font-medium">Get In Touch</span>
            <div className="h-0.5 w-8 bg-teal"></div>
          </div>
          <h2 className="font-heading font-800 text-navy text-4xl lg:text-5xl mb-4">
            Contact &amp; Location
          </h2>
          <p className="text-charcoal/60 font-body text-lg max-w-xl mx-auto">
            Reach out for your free moving quote, or just to ask a question. We respond fast.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* Left - Contact Info + Map */}
          <div className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="rounded-2xl overflow-hidden border border-navy/10 mb-6" style={{ height: '320px' }}>
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2822.6491523025857!2d-93.27290902358!3d44.97986507107!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x52b32d4b46be45a9%3A0x123456789!2s121%20Washington%20Ave%20S%2C%20Minneapolis%2C%20MN%2055401!5e0!3m2!1sen!2sus!4v1710000000001!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="TrueCare Moving Services Location"
              ></iframe>
            </div>

            <div className="bg-white rounded-2xl p-7 border border-navy/10">
              <h3 className="font-heading font-700 text-navy text-lg mb-6">Contact Information</h3>
              <div className="space-y-5">
                {[
                  { icon: 'ri-map-pin-line', label: 'Address', value: '121 Washington Ave S #332, Minneapolis, MN 55401', href: null },
                  { icon: 'ri-phone-line', label: 'Phone', value: '(612) 367-4481', href: 'tel:+16123674481' },
                  { icon: 'ri-mail-line', label: 'Email', value: 'truecaremovingservice@gmail.com', href: 'mailto:truecaremovingservice@gmail.com' },
                  { icon: 'ri-time-line', label: 'Hours', value: 'Mon–Sat: 7:00 AM – 10:00 PM', href: null },
                ].map((item) => (
                  <div key={item.label} className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-teal/10 rounded-xl shrink-0">
                      <i className={`${item.icon} text-teal text-base`}></i>
                    </div>
                    <div>
                      <div className="font-heading font-600 text-navy text-xs uppercase tracking-wide mb-1">{item.label}</div>
                      {item.href ? (
                        <a href={item.href} className="text-charcoal/70 font-body text-sm hover:text-teal transition-colors cursor-pointer break-all">
                          {item.value}
                        </a>
                      ) : (
                        <span className="text-charcoal/70 font-body text-sm">{item.value}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right - Quote Form */}
          <div className={`transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="bg-navy rounded-2xl p-8 h-full" id="quote">
              <h3 className="font-heading font-700 text-white text-xl mb-2">Get Your Free Moving Quote</h3>
              <p className="text-text-muted font-body text-sm mb-7">No obligation. No hidden fees. Just an honest estimate.</p>

              {submitted ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-teal/20 rounded-2xl mb-4">
                    <i className="ri-check-double-line text-teal text-3xl"></i>
                  </div>
                  <h4 className="font-heading font-700 text-white text-lg mb-2">Quote Request Sent!</h4>
                  <p className="text-text-muted font-body text-sm">We&apos;ll get back to you within 1–2 hours. Or call us at (612) 367-4481 for immediate help.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-text-muted font-body text-xs mb-1.5">Full Name *</label>
                      <input
                        type="text"
                        name="name"
                        required
                        value={formState.name}
                        onChange={(e) => setFormState((p) => ({ ...p, name: e.target.value }))}
                        placeholder="John Smith"
                        className="w-full bg-white/10 border border-white/20 text-white placeholder-text-muted rounded-lg px-4 py-3 font-body text-sm focus:border-teal focus:outline-none transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-text-muted font-body text-xs mb-1.5">Phone *</label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        value={formState.phone}
                        onChange={(e) => setFormState((p) => ({ ...p, phone: e.target.value }))}
                        placeholder="(612) 000-0000"
                        className="w-full bg-white/10 border border-white/20 text-white placeholder-text-muted rounded-lg px-4 py-3 font-body text-sm focus:border-teal focus:outline-none transition-colors"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-text-muted font-body text-xs mb-1.5">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formState.email}
                      onChange={(e) => setFormState((p) => ({ ...p, email: e.target.value }))}
                      placeholder="john@example.com"
                      className="w-full bg-white/10 border border-white/20 text-white placeholder-text-muted rounded-lg px-4 py-3 font-body text-sm focus:border-teal focus:outline-none transition-colors"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-text-muted font-body text-xs mb-1.5">Move Date</label>
                      <input
                        type="date"
                        name="moveDate"
                        value={formState.moveDate}
                        onChange={(e) => setFormState((p) => ({ ...p, moveDate: e.target.value }))}
                        className="w-full bg-white/10 border border-white/20 text-white placeholder-text-muted rounded-lg px-4 py-3 font-body text-sm focus:border-teal focus:outline-none transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-text-muted font-body text-xs mb-1.5">Service Type</label>
                      <select
                        name="serviceType"
                        value={formState.serviceType}
                        onChange={(e) => setFormState((p) => ({ ...p, serviceType: e.target.value }))}
                        className="w-full bg-charcoal border border-white/20 text-white rounded-lg px-4 py-3 font-body text-sm focus:border-teal focus:outline-none transition-colors cursor-pointer"
                      >
                        <option value="">Select service</option>
                        <option>Local Moving</option>
                        <option>Residential Moving</option>
                        <option>Apartment Moving</option>
                        <option>Commercial Moving</option>
                        <option>Full-Service Moving</option>
                        <option>Heavy-Item Moving</option>
                        <option>Last-Minute Moving</option>
                        <option>Loading &amp; Unloading</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-text-muted font-body text-xs mb-1.5">Additional Details</label>
                    <textarea
                      name="message"
                      maxLength={500}
                      value={formState.message}
                      onChange={(e) => setFormState((p) => ({ ...p, message: e.target.value }))}
                      placeholder="Describe your move — number of rooms, special items, from/to addresses..."
                      rows={3}
                      className="w-full bg-white/10 border border-white/20 text-white placeholder-text-muted rounded-lg px-4 py-3 font-body text-sm focus:border-teal focus:outline-none transition-colors resize-none"
                    ></textarea>
                    <div className="text-text-muted font-body text-xs mt-1 text-right">{formState.message.length}/500</div>
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-teal hover:bg-teal-dark text-navy font-heading font-700 py-4 rounded-xl transition-all duration-200 text-sm cursor-pointer whitespace-nowrap disabled:opacity-60"
                  >
                    {submitting ? 'Sending...' : 'Request Free Quote'}
                  </button>
                  <p className="text-text-muted/60 font-body text-xs text-center">
                    Or call us directly: <a href="tel:+16123674481" className="text-teal hover:underline cursor-pointer">(612) 367-4481</a>
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MapContact;
